import { useCallback, useEffect, useRef } from 'react'
import * as React from 'react'
import cx from 'classnames'

import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { AlmanacFile as File } from 'types'
import { handleSuccessResponseWithMessage } from 'api/utils/handleSuccessResponseWithMessage'
import { FolderClient } from 'api/clients/FolderClient'
import { buildErrorMessage } from 'utils'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SetState } from 'types'
import { useHistory } from 'react-router-dom'
import { useWorkspace } from 'hooks/useWorkspace'
import { Button } from 'components/Button/Button'
import { ReactComponent as CancelIcon } from 'assets/icons/close16.svg'
import { AncestorProject } from 'types'
import Styles from './NewFolderInput.module.scss'
import { FolderIcon } from 'components/Icons/FolderIcon'

interface NewFolderInputProps {
  className?: string
  files: File[]
  folderId?: string
  folders?: File[]
  setFiles: SetState<File[]>
  setFolders?: SetState<File[]>
  setShowNewFolderInput: SetState<boolean>
  workspaceId: number
  cancelable?: boolean
  onFolderCreation?: (folder: AncestorProject) => void
}

export const NewFolderInput = ({
  workspaceId,
  setShowNewFolderInput,
  setFiles,
  files,
  className,
  setFolders,
  folders,
  folderId,
  cancelable = false,
  onFolderCreation,
}: NewFolderInputProps) => {
  const history = useHistory()
  const workspace = useWorkspace(workspaceId)
  const inputRef = useRef<HTMLInputElement>(null)
  const workspaceKey = workspace?.key

  const createFolder = useCallback(
    async ({ title }: { title: string }) => {
      if (!workspaceKey) return

      const response = await FolderClient.createFolder({ title, workspaceKey, parentKey: folderId })
      handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        setFiles([response.data.folder, ...files])
        if (!!setFolders && !!folders) setFolders([response.data.folder, ...folders])
        setShowNewFolderInput(false)
        const folderUrl = response?.data?.folder?.url
        if (folderUrl) {
          history.push(folderUrl)
        }
        const successMessage = (
          <>
            🎉 <b>{title}</b> created!
          </>
        )
        handleSuccessResponseWithMessage(successMessage)(response)
        return response.data.folder
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceKey, files, setShowNewFolderInput, setFiles, folderId, folders, setFolders],
  )

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!inputRef.current?.value) return

    if (e.key === 'Enter') {
      e.preventDefault()
      const newFolder = await createFolder({ title: inputRef.current.value })
      newFolder && onFolderCreation?.(newFolder)
    }
  }

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!inputRef.current?.contains(e.target as Node)) {
        if (!inputRef.current) return

        if (inputRef.current.value) {
          createFolder({ title: inputRef.current.value })
          return
        }

        setShowNewFolderInput(false)
      }
    },
    [inputRef, createFolder, setShowNewFolderInput],
  )

  useEffect(() => {
    document.addEventListener('click', handleClick)
    inputRef.current?.focus()

    return () => document.removeEventListener('click', handleClick)
  }, [handleClick])

  const handleCancelClick = () => {
    setShowNewFolderInput(false)
  }

  return (
    <div className={cx(Styles.docRow, className)}>
      <div className={Styles.newFolderInput}>
        <FolderIcon className={Styles.newFolderIcon} />
        <input id={'new-folder'} ref={inputRef} placeholder={'Untitled Folder'} onKeyDown={onKeyDown} />
        {cancelable && (
          <Button
            className={Styles.cancelButton}
            variant="tertiary"
            onPress={handleCancelClick}
            frontIcon={CancelIcon}
          />
        )}
      </div>
      <div />
      <div />
      <div />
      <div />
    </div>
  )
}
